import { ArrowUp, LoaderCircle, Square, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/_shared/lib/utils';

import { SkillMenu } from './SkillMenu';

interface PendingSkill {
  id: string;
  title: string;
}

interface MessageInputProps {
  onSend: (content: string, invokedSkillIds?: string[]) => void;
  disabled: boolean;
  variant?: 'hero' | 'chat' | 'sidebar';
  /** When provided and the input is disabled, shows an inline status text above the textarea. */
  thinkingText?: string;
  /** Skills already loaded into this chat session (non-removable, shown dimmed). */
  loadedSkills?: { id: string; title: string }[];
  /** Needed by SkillMenu to filter against the workspace. When absent, slash menu is suppressed. */
  workspaceSlug?: string;
  /** Gate: only show slash menu when workspace has ≥1 promoted skill. */
  hasPromotedSkills?: boolean;
  /** When provided AND `disabled === true`, replaces the spinner with a stop button. */
  onStop?: () => void;
  /** Optional content to prefill into the textarea — applied only when the textarea is empty. */
  prefillValue?: string;
  /** Bumped by the parent each time a new prefill should be applied (allows the same value to retrigger). */
  prefillNonce?: number;
}

export function MessageInput({
  onSend,
  disabled,
  variant = 'chat',
  thinkingText,
  loadedSkills = [],
  workspaceSlug,
  hasPromotedSkills = false,
  onStop,
  prefillValue,
  prefillNonce,
}: MessageInputProps): React.ReactElement {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Slash-invoke state
  const [slashQuery, setSlashQuery] = useState<string | null>(null);
  const [pendingSkills, setPendingSkills] = useState<PendingSkill[]>([]);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  // Reset height when value is cleared (after send)
  useEffect(() => {
    if (!value && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value]);

  // Track latest value without retriggering the prefill effect (canonical "latest ref" pattern).
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  });

  useEffect(() => {
    if (prefillValue && !valueRef.current.trim()) {
      setValue(prefillValue);
      requestAnimationFrame(adjustHeight);
    }
  }, [prefillNonce, prefillValue, adjustHeight]);

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    const ids = pendingSkills.map((s) => s.id);
    onSend(trimmed, ids.length > 0 ? ids : undefined);
    setValue('');
    setPendingSkills([]);
    setSlashQuery(null);
  }, [value, disabled, onSend, pendingSkills]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (slashQuery !== null && (e.key === 'Enter' || e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit, slashQuery],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setValue(newValue);
      adjustHeight();

      // Slash-invoke trigger: "/" as first character when hasPromotedSkills is true
      if (hasPromotedSkills && workspaceSlug) {
        if (newValue.startsWith('/') && !newValue.includes(' ')) {
          setSlashQuery(newValue.slice(1));
        } else {
          setSlashQuery(null);
        }
      } else {
        setSlashQuery(null);
      }
    },
    [adjustHeight, hasPromotedSkills, workspaceSlug],
  );

  const handleMenuSelect = useCallback((skill: { id: string; title: string }) => {
    // Avoid adding duplicates
    setPendingSkills((prev) => {
      if (prev.some((s) => s.id === skill.id)) return prev;
      return [...prev, skill];
    });
    // Strip the /query prefix from textarea; preserve any non-slash content
    setValue((v) => (v.startsWith('/') ? '' : v));
    setSlashQuery(null);
    // Return focus to textarea
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, []);

  const handleMenuClose = useCallback(() => {
    setSlashQuery(null);
  }, []);

  const removePendingSkill = useCallback((id: string) => {
    setPendingSkills((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // Send requires typed text — a skill-only send would be a no-op in the
  // agent (injected skill body with no user question). Pending-skill chips
  // stay visible but don't unlock the button until the user types.
  const hasContent = value.trim().length > 0;
  const isHero = variant === 'hero';
  const isSidebar = variant === 'sidebar';

  // Chip strip: already-loaded skills (non-removable) + pending skills (removable)
  const showChips = loadedSkills.length > 0 || pendingSkills.length > 0;
  const alreadyLoadedSet = useMemo(() => new Set(loadedSkills.map((s) => s.id)), [loadedSkills]);

  return (
    <div className={cn(isHero ? 'w-full' : isSidebar ? 'px-3 pt-1 pb-3' : 'px-4 pt-2 pb-4')}>
      <div className={cn('mx-auto w-full', !isHero && !isSidebar && 'max-w-4xl')}>
        {/* Floating container */}
        <div
          ref={containerRef}
          className={cn(
            'group relative border transition-all duration-200',
            'focus-within:border-brand/45 focus-within:shadow-[0_0_0_1px_rgba(16,94,213,0.14)]',
            isHero
              ? 'rounded-[1.75rem] border-border/75 bg-white/92 shadow-[0_24px_60px_-42px_rgba(47,66,112,0.24)]'
              : isSidebar
                ? 'rounded-xl border-border/60 bg-card'
                : 'rounded-2xl border-border/60 bg-card',
          )}
        >
          {/* SkillMenu anchored above the container */}
          {slashQuery !== null && workspaceSlug && (
            <SkillMenu
              query={slashQuery}
              alreadyLoadedIds={alreadyLoadedSet}
              onSelect={handleMenuSelect}
              onClose={handleMenuClose}
            />
          )}

          {/* Chip strip: loaded + pending skills */}
          {showChips && (
            <div className={cn('flex flex-wrap gap-1 pt-2', isHero ? 'px-6' : 'px-3')} data-testid="skill-chip-strip">
              {/* Already loaded: non-removable, dimmed */}
              {loadedSkills.map((skill) => (
                <span
                  key={skill.id}
                  className="inline-flex items-center gap-1 rounded-full border border-border/50 bg-muted/60 px-2 py-0.5 text-xs text-muted-foreground"
                  data-testid={`loaded-skill-chip-${skill.id}`}
                >
                  {skill.title}
                </span>
              ))}
              {/* Pending: removable, pre-send */}
              {pendingSkills.map((skill) => (
                <span
                  key={skill.id}
                  className="inline-flex items-center gap-1 rounded-full border border-brand/30 bg-brand/10 px-2 py-0.5 text-xs text-brand"
                  data-testid={`pending-skill-chip-${skill.id}`}
                >
                  {skill.title}
                  <button
                    type="button"
                    onClick={() => removePendingSkill(skill.id)}
                    aria-label={`Remove skill ${skill.title}`}
                    className="ml-0.5 rounded-full hover:bg-brand/20"
                    data-testid={`remove-skill-chip-${skill.id}`}
                  >
                    <X className="size-2.5" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Inline thinking status */}
          {thinkingText && disabled && (
            <div className="animate-pulse px-4 pt-2 pb-1 text-xs text-muted-foreground" aria-live="polite">
              {thinkingText}
            </div>
          )}
          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={
              isHero
                ? "Describe what you'd like to investigate..."
                : isSidebar
                  ? 'Ask about this dashboard...'
                  : 'Ask about your data pipeline...'
            }
            rows={isHero ? 2 : 1}
            className={cn(
              'w-full resize-none bg-transparent text-foreground outline-none',
              'placeholder:text-muted-foreground/65 disabled:opacity-50',
              isHero
                ? 'min-h-28 px-6 pt-5 pb-2 text-[1.0625rem] leading-7'
                : isSidebar
                  ? 'px-3 pt-2 pb-1 text-xs'
                  : 'px-4 pt-3 pb-1 text-sm',
            )}
          />

          {/* Bottom bar: branding left, send button right */}
          <div
            className={cn(
              'flex items-end justify-between',
              isHero ? 'px-6 pb-4' : isSidebar ? 'px-2.5 pb-2' : 'px-3 pb-2.5',
            )}
          >
            {/* Left: model/branding label — hidden in sidebar */}
            {!isSidebar && (
              <span
                className={cn(
                  'tracking-wide text-muted-foreground/55 select-none',
                  isHero ? 'text-xs font-medium' : 'text-[0.6875rem]',
                )}
              >
                Rudder AI <span className="text-brand/50">beta</span>
              </span>
            )}
            {isSidebar && <span aria-hidden />}

            {/* Right: stop or send button */}
            {disabled && onStop ? (
              <button
                onClick={onStop}
                className={cn(
                  'flex items-center justify-center transition-all duration-150',
                  isHero ? 'h-11 w-11 rounded-2xl' : isSidebar ? 'h-6 w-6 rounded-md' : 'h-7 w-7 rounded-lg',
                  // Stop button uses bg-foreground (not bg-destructive) — it's a neutral interrupt, not destructive.
                  'bg-foreground text-background hover:bg-foreground/85',
                )}
                aria-label="Stop generating"
              >
                <Square size={isSidebar ? 10 : isHero ? 14 : 12} strokeWidth={0} fill="currentColor" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={disabled || !hasContent}
                className={cn(
                  'flex items-center justify-center transition-all duration-150',
                  isHero ? 'h-11 w-11 rounded-2xl' : isSidebar ? 'h-6 w-6 rounded-md' : 'h-7 w-7 rounded-lg',
                  hasContent && !disabled
                    ? 'bg-brand text-primary-foreground shadow-[0_18px_36px_-24px_rgba(16,94,213,0.85)] hover:bg-brand/90'
                    : 'bg-muted-foreground/10 text-muted-foreground/30',
                  'disabled:cursor-not-allowed',
                )}
                aria-label={disabled ? 'Processing...' : 'Send message'}
              >
                {disabled ? (
                  <LoaderCircle size={isSidebar ? 12 : isHero ? 16 : 14} strokeWidth={2.5} className="animate-spin" />
                ) : (
                  <ArrowUp size={isSidebar ? 12 : isHero ? 16 : 14} strokeWidth={2.5} />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Hint text below input — hidden in sidebar */}
        {!isHero && !isSidebar && (
          <p className="mt-1.5 text-center text-[0.625rem] text-muted-foreground/50">
            Enter to send · Shift+Enter for new line
            {hasPromotedSkills && ' · / to invoke a skill'}
          </p>
        )}
        {isHero && hasPromotedSkills && (
          <p className="mt-2 text-center text-xs text-muted-foreground/60">
            Type <span className="font-mono text-foreground/70">/</span> to invoke a skill
          </p>
        )}
      </div>
    </div>
  );
}
